import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { QRCode } from 'react-qrcode-logo';

import { ReactComponent as EvseIcon } from '../../assets/icons/charger-icon.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/copy.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download.svg';
import { brandImage } from '../../config/branding';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ocpiApi from '../../utils/api/ocpi';
import qrApi from '../../utils/api/qr';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import ModalBox from '../modal-box/ModalBox';
import SelectField from '../select-field/SelectField';

import './qr-configuration-modal.scss';

export type AdhocType = 'mobility_adhoc' | 'network_adhoc';

export interface UpdateQrConfigPayload {
  evseId: string;
  adhocType: AdhocType;
  connectorId?: string | number;
  tenantId?: string | null;
  tenantName?: string | null;
}

interface QrConfigurationModalProperties {
  evseId: string;
  connectorId: string | number;
  connectorType: string;
  handleClose: () => void;
}

/**
 * Copies a value to the clipboard and shows a notification.
 * @param {string} value - The text to copy.
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} t - i18n translation function.
 * @returns {void}
 */
const copyToClipboard = (value: string, dispatch: Function, t: Function): void => {
  navigator.clipboard.writeText(value).then(() => {
    dispatch(
      setNotificationMessage({
        message: t('copied', 'Copied to clipboard'),
        status: true,
        type: 'success',
        code: 200,
      })
    );
  });
};

/**
 * QR Configuration Modal Component
 * @param {QrConfigurationModalProperties} props - The component properties.
 * @returns {React.ReactElement} The rendered component.
 */
export default function QrConfigurationModal({
  evseId,
  connectorId,
  connectorType,
  handleClose,
}: QrConfigurationModalProperties): JSX.Element {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [qrConfig, setQrConfig] = useState<any>(null);
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const qrReference = useRef<HTMLDivElement>(null);

  /**
   * Fetches initial data for the QR configuration modal.
   * @returns {Promise<void>} A promise that resolves when data is loaded.
   */
  const fetchInitialData = async (): Promise<void> => {
    setLoading(true);
    try {
      let currentConfig: any = null;
      try {
        const configResponse = await qrApi.getProxyUrlByEvseId(evseId);
        currentConfig = configResponse?.data?.data || null;
      } catch (error: any) {
        console.warn('QR Config not found or error', error);
      }
      setQrConfig(currentConfig);

      let fetchedConnections: any[] = [];
      try {
        const response = await ocpiApi.getCpoEmspConnections();
        fetchedConnections =
          response?.data?.data?.emspConnections || response?.data?.emspConnections || [];
      } catch (error: any) {
        if (error.response?.status === 404) {
          dispatch(
            setNotificationMessage({
              message: t('noCpoTenantConfigured', 'No CPO tenant configured'),
              status: true,
              type: 'error',
              code: 404,
            })
          );
        } else {
          console.warn('Failed to fetch EMSP connections', error);
        }
      }
      setConnections(fetchedConnections);

      // Determine pre-selected connection based on existing config
      if (
        currentConfig?.adhocType === 'mobility_adhoc' &&
        currentConfig?.mobilityTenantInformation?.length > 0
      ) {
        const [{ tenantId }] = currentConfig.mobilityTenantInformation;
        const matchedConn = fetchedConnections.find((c) => c.emspTenantId === tenantId);

        if (matchedConn) {
          setSelectedConnectionId(matchedConn.connectionId);
        } else {
          setSelectedConnectionId('network_adhoc');
        }
      } else {
        setSelectedConnectionId('network_adhoc');
      }
    } catch (error) {
      console.error('Failed to load initial data for QR config:', error);
      dispatch(
        setNotificationMessage({
          message: t('failedToLoadData', 'Failed to load configuration data'),
          status: true,
          type: 'error',
          code: 500,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [evseId]);

  const currentSelectedConn = connections.find((c) => c.connectionId === selectedConnectionId);

  // For API Payload
  const payloadTenantName = currentSelectedConn?.domainName || null;
  const payloadTenantId = currentSelectedConn?.emspTenantId || null;

  // For Connection Details display
  const isNetworkAdhoc = selectedConnectionId === 'network_adhoc';
  const displayFallback = currentSelectedConn
    ? [currentSelectedConn.emspPartyId, currentSelectedConn.emspCountryCode]
      .filter(Boolean)
      .join(' - ') || 'Unknown'
    : t('unknown', 'Unknown');
  const displayConnectionName = currentSelectedConn?.domainName || displayFallback;
  const displayTenantId = payloadTenantId || '-';


  /**
   * Validates selection and opens the confirmation dialog.
   * @returns {void}
   */
  const handleConfirmRequest = (): void => {
    if (!selectedConnectionId) {
      dispatch(
        setNotificationMessage({
          message: t('pleaseSelectConnection', 'Please select a connection'),
          status: true,
          type: 'error',
          code: 400,
        })
      );
      return;
    }
    setShowConfirm(true);
  };

  /**
   * Performs the actual save after user confirms.
   * @returns {Promise<void>} A promise that resolves when saved.
   */
  const handleConfirmedSave = async (): Promise<void> => {
    setShowConfirm(false);
    setSaving(true);
    try {
      const payload: UpdateQrConfigPayload = {
        evseId,
        connectorId,
        adhocType: isNetworkAdhoc ? 'network_adhoc' : 'mobility_adhoc',
        tenantId: isNetworkAdhoc ? null : payloadTenantId,
        tenantName: isNetworkAdhoc ? null : payloadTenantName,
      };

      await qrApi.saveQrConfig(payload);

      dispatch(
        setNotificationMessage({
          message: t('qrConfigSaved', 'QR Configuration saved successfully'),
          status: true,
          type: 'success',
          code: 200,
        })
      );
      handleClose();
    } catch (error) {
      console.error('Failed to save QR config:', error);
      dispatch(
        setNotificationMessage({
          message: t('failedToSave', 'Failed to save QR configuration'),
          status: true,
          type: 'error',
          code: 500,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  // Connection dropdown options
  const connectionOptions = [
    // { value: 'network_adhoc', label: 'Network Adhoc' },
    ...connections.map((conn) => {
      const fallbackName =
        [conn.emspPartyId, conn.emspCountryCode].filter(Boolean).join(' - ') ||
        t('unknownConnection', 'Unknown Connection');
      const domainName = conn.domainName || fallbackName;
      const emspTenantId = conn.emspTenantId || t('noTenantId', 'No Tenant ID');
      const labelText = `${emspTenantId} (${domainName})`;
      return {
        value: conn.connectionId,
        label: labelText,
        labelNode: (
          <span className="connection-option-label">
            {labelText}
          </span>
        ),
      };
    }),
  ];

  const modalContent = (
    <div className="qr-config-modal-content" dir={i18n.dir()}>
      {loading ? (
        <div className="qr-config-loading">
          <BatteryLoader />
        </div>
      ) : (
        <>
          {/* EVSE Details Card */}
          <div className="qr-evse-card">
            <div className="qr-evse-icon">
              <EvseIcon />
            </div>
            <div className="qr-evse-info">
              <div className="qr-evse-field">
                <span className="qr-evse-field-label">{t('evseId', 'EVSE ID')}</span>
                <span className="qr-evse-field-value">{evseId || '-'}</span>
              </div>
              <div className="qr-evse-field">
                <span className="qr-evse-field-label">{t('connector', 'Connector')}</span>
                <span className="qr-evse-field-value">
                  {connectorId} ({connectorType || '-'})
                </span>
              </div>
            </div>
          </div>

          {/* QR Configuration Section */}
          <div className="qr-config-section">
            <div className="qr-config-section-header">
              <h4>{t('qrConfiguration', 'QR Configuration')}</h4>
              <p>
                {t(
                  'qrConfigurationDescription',
                  'Assign an eMSP connection to configure the routing for this QR code.'
                )}
              </p>
            </div>

            <div className="qr-config-main">
              {/* Left: QR Code Preview */}
              <div className="qr-config-preview">
                <div className="qr-preview-header">
                  <span className="qr-preview-title">{t('qrCode', 'QR Code')}</span>
                  {qrConfig?.proxyUrl && (
                    <button
                      type="button"
                      className="qr-download-btn"
                      onClick={() => {
                        const canvas = qrReference.current?.querySelector('canvas');
                        if (canvas) {
                          const dataUrl = canvas.toDataURL('image/png');
                          const link = document.createElement('a');
                          link.href = dataUrl;
                          link.download = `qr-${evseId}.png`;
                          link.click();
                        }
                      }}>
                      <DownloadIcon /> {t('download', 'Download')}
                    </button>
                  )}
                </div>

                <div className="qr-image-wrapper" ref={qrReference}>
                  {qrConfig?.proxyUrl ? (
                    <QRCode
                      size={180}
                      ecLevel="H"
                      qrStyle="squares"
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                      logoWidth={40}
                      logoHeight={40}
                      logoOpacity={1}
                      quietZone={10}
                      value={qrConfig.proxyUrl}
                      logoImage={brandImage.qrLogo}
                      removeQrCodeBehindLogo
                    />
                  ) : (
                    <div className="qr-not-configured">
                      {t('qrNotConfigured', 'QR Not Configured')}
                    </div>
                  )}
                </div>

                {qrConfig?.proxyUrl && (
                  <div className="qr-meta-field url">
                    <span className="qr-meta-label">{t('qrCodeUrl', 'QR Code URL')}</span>
                    <span className="qr-meta-value">
                      {qrConfig.proxyUrl}
                      <button
                        type="button"
                        className="qr-copy-btn"
                        onClick={() => copyToClipboard(qrConfig.proxyUrl, dispatch, t)}>
                        <CopyIcon />
                      </button>
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Configuration Form */}
              <div className="qr-config-form">
                <div className="qr-field-group">
                  <div className="qr-field-label-row">
                    <span className="qr-field-label">
                      {t('assignEmspConnection', 'Assign eMSP Connection')}
                    </span>
                    <span className="qr-tooltip-wrapper">
                      <span className="qr-tooltip-icon">ℹ</span>
                      <span className="qr-tooltip-text">
                        {t(
                          'adhocChargingOnly',
                          'This configuration is only applicable for the adhoc charging'
                        )}
                      </span>
                    </span>
                  </div>
                  <SelectField
                    name="assignQrConnection"
                    options={connectionOptions}
                    selectedValue={selectedConnectionId}
                    onChangeValue={(_name: string, value: any) =>
                      setSelectedConnectionId(value as string)
                    }
                    placeholder={t('selectConnection', 'Select Connection')}
                  />
                </div>

                {/* Connection Details Card */}
                <div className="connection-details-card">
                  <p className="connection-details-title">
                    {t('connectionDetails', 'Connection Details')}
                  </p>

                  <div className="detail-row">
                    <span className="label">{t('connection', 'Connection')}</span>
                    <span className="value">
                      {isNetworkAdhoc ? '-' : t('mobilityAdhoc', 'Mobility Adhoc')}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">{t('tenantName', 'Tenant Name')}</span>
                    <span className="value">
                      {isNetworkAdhoc ? '-' : displayConnectionName}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">{t('tenantId', 'Tenant ID')}</span>
                    <span className="value">{isNetworkAdhoc ? '-' : displayTenantId}</span>
                  </div>
                </div>

                <div className="qr-config-info-note">
                  <span className="info-icon">ℹ</span>
                  <span>
                    {t(
                      'qrConnectionChangeNote',
                      'Changing the connection will update the QR routing configuration. The physical QR code does not need to be replaced.'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* EMSP Assignment Confirmation Dialog */}
          {showConfirm && (
            <div className="emsp-confirm-overlay">
              <div className="emsp-confirm-dialog">
                <div className="emsp-confirm-icon-wrap">
                  <span className="emsp-confirm-icon">⚡</span>
                </div>
                <h4 className="emsp-confirm-title">
                  {t('confirmEmspConnection', 'Confirm EMSP Connection')}
                </h4>
                <p className="emsp-confirm-body">
                  <Trans
                    ns="sidePanel"
                    i18nKey="confirmQrAssignment"
                    values={{
                      connectionName: isNetworkAdhoc
                        ? t('networkAdhoc', 'Network Adhoc')
                        : displayConnectionName,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p className="emsp-confirm-body">
                  <Trans
                    ns="sidePanel"
                    i18nKey="confirmQrUsage"
                    values={{
                      connectionName: isNetworkAdhoc
                        ? t('networkAdhoc', 'Network Adhoc')
                        : displayConnectionName,
                    }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p className="emsp-confirm-question">
                  {t('confirmContinue', 'Are you sure you want to continue?')}
                </p>
                <div className="emsp-confirm-actions">
                  <MyButton
                    label={t('cancel', 'Cancel')}
                    buttonType="primary-outline"
                    onClickFunc={() => setShowConfirm(false)}
                    disableButton={saving}
                  />
                  <MyButton
                    label={t('confirm', 'Confirm')}
                    buttonType="primary"
                    onClickFunc={handleConfirmedSave}
                    loading={saving}
                    disableButton={saving}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="qr-config-actions">
            <MyButton
              label={t('cancel', 'Cancel')}
              buttonType="primary-outline"
              onClickFunc={handleClose}
              disableButton={saving}
            />
            <MyButton
              label={t('save', 'Save')}
              buttonType="primary"
              onClickFunc={handleConfirmRequest}
              loading={saving}
              disableButton={loading || saving}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <ModalBox
      open
      header
      title={t('qrConfigurationTitle', 'QR Code Configuration')}
      closeModal={handleClose}
      notification={false}
      content={modalContent}
    />
  );
}
